import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  FormProvider,
  useForm,
  type FieldPath,
  type UseFormRegisterReturn,
} from "react-hook-form";
import {
  useCountryCodes,
  usePanelId,
  useSignupFields,
  useSignUpPanelist,
} from "../api";
import { DynamicField } from "../components/forms/DynamicField";
import {
  initialDynamicFieldValue,
  isMultiValueDynamicField,
} from "../components/forms/dynamicFields";
import { ErrorState, SkeletonBlock } from "../components/ui/AsyncState";
import { usePanelPath } from "../routing";
import type {
  ProfileField,
  ProfileFieldsData,
  ProfileFieldValue,
  SignupInput,
} from "../types";
import { buildProfileFieldRows } from "../utils/profileFields";
import { isValidUkContactPhone, normalizePhone } from "../utils/validation";

type ProfileFieldValues = Record<string, ProfileFieldValue>;

type SignupFormValues = {
  dateOfBirth: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  countryCode: string;
  profileFields: ProfileFieldValues;
  terms: boolean;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as
  | string
  | undefined;

type TurnstileRenderOptions = {
  callback?: (token: string) => void;
  "error-callback"?: () => void;
  "expired-callback"?: () => void;
  sitekey: string;
  theme?: "light" | "dark" | "auto";
};

type TurnstileApi = {
  ready: (callback: () => void) => void;
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const defaultFormState: SignupFormValues = {
  dateOfBirth: "",
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
  countryCode: "",
  profileFields: {},
  terms: false,
};

interface SignupSectionProps {
  currentEmail: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function SignupSection({
  currentEmail,
  onSuccess,
  onCancel,
}: SignupSectionProps) {
  const panelPath = usePanelPath();
  const panelId = usePanelId();
  const signupFieldsQuery = useSignupFields(panelId);
  const signUpPanelist = useSignUpPanelist(panelId);
  const countryCodes = useCountryCodes({
    select: (d) => {
      const locale = navigator.language.split("-")[1];
      const usuals = ["GB"];
      const suggested = d?.find(
        (c) => c.countryCode === locale || usuals.includes(c.countryCode),
      );
      const others = d?.filter((c) => c.countryCode !== locale);
      if (suggested) {
        return [
          suggested,
          ...others.sort((a, b) => a.name.localeCompare(b.name)),
        ];
      }
      return d?.sort((a, b) => a.name.localeCompare(b.name));
    },
  });
  const signupFields = signupFieldsQuery.data;
  const turnstileContainerId = useId();
  const turnstileWidgetId = useRef<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const form = useForm<SignupFormValues>({
    defaultValues: {
      ...defaultFormState,
      email: currentEmail,
    },
  });
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = form;

  useEffect(() => {
    reset((currentValues) => ({
      ...currentValues,
      email: currentEmail,
    }));
  }, [currentEmail, reset]);

  useEffect(() => {
    if (!signupFields) {
      return;
    }

    reset((currentValues) => ({
      ...currentValues,
      profileFields: readInitialSignupFieldValues(signupFields),
    }));
  }, [signupFields, reset]);

  useEffect(() => {
    if (!turnstileSiteKey) {
      return;
    }

    const container = document.getElementById(turnstileContainerId);
    const api = window.turnstile;
    if (!container || !api || turnstileWidgetId.current) {
      return;
    }

    api.ready(() => {
      if (turnstileWidgetId.current) {
        return;
      }

      turnstileWidgetId.current = api.render(container, {
        callback: (token) => {
          setTurnstileToken(token);
          setTurnstileError(null);
        },
        "error-callback": () => {
          setTurnstileToken("");
          setTurnstileError(
            "Bot protection could not be verified. Please try again.",
          );
        },
        "expired-callback": () => {
          setTurnstileToken("");
          setTurnstileError(
            "Bot protection expired. Please complete it again.",
          );
          if (turnstileWidgetId.current) {
            window.turnstile?.reset(turnstileWidgetId.current);
          }
        },
        sitekey: turnstileSiteKey,
        theme: "light",
      });
    });

    return () => {
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }
    };
  }, [turnstileContainerId]);

  const canSubmit =
    Boolean(signupFields) &&
    !signUpPanelist.isPending &&
    (!turnstileSiteKey || Boolean(turnstileToken));

  function submitSignup(values: SignupFormValues) {
    if (turnstileSiteKey && !turnstileToken) {
      setTurnstileError("Please complete the bot protection check.");
      return;
    }

    const payload: SignupInput = {
      consent: {
        privacyAccepted: true,
        termsAccepted: true,
      },
      dateOfBirth: values.dateOfBirth,
      email: values.email.trim().toLowerCase(),
      firstName: values.firstName.trim(),
      countryCode: values.countryCode.trim(),
      lastName: values.lastName.trim(),
      phone: normalizePhone(values.phone),
      profileFields: values.profileFields,
      turnstileToken: turnstileToken || undefined,
    };

    setTurnstileError(null);

    signUpPanelist.mutate(payload, {
      onSuccess: () => {
        if (turnstileWidgetId.current) {
          window.turnstile?.reset(turnstileWidgetId.current);
        }
        setTurnstileToken("");
        onSuccess();
      },
    });
  }

  return (
    <AuthSignupShell>
      <div className="auth-panel signup-panel">
        <div className="auth-eyebrow">Step 2 of 2</div>
        <h1 className="auth-title title-md">Complete sign up</h1>
        <div className="auth-copy">
          Finish your panel profile to start using the portal.
        </div>

        <FormProvider {...form}>
          <form
            className="signup-form"
            noValidate
            onSubmit={handleSubmit(submitSignup)}
          >
            <div className="section-lbl">About you</div>
            <div className="field-row">
              <TextField
                autoComplete="given-name"
                error={errors.firstName?.message}
                label="First name"
                placeholder="Jane"
                registration={register("firstName", {
                  required: "Please enter your first name.",
                })}
                required
              />
              <TextField
                autoComplete="family-name"
                error={errors.lastName?.message}
                label="Last name"
                placeholder="Doe"
                registration={register("lastName", {
                  required: "Please enter your last name.",
                })}
                required
              />
            </div>

            <div className="field-row">
              <TextField
                autoComplete="email"
                error={errors.email?.message}
                label="Email address"
                readOnly
                registration={register("email", {
                  required: "Email is required.",
                  validate: (value: string) => {
                    if (!EMAIL_RE.test(value.trim())) {
                      return "Please enter a valid email address.";
                    }
                    const host = value.split("@")[1];
                    if (
                      host === "privaterelay.appleid.com" ||
                      host === "private.icloud.com"
                    ) {
                      return "Mailbox is not allowed. Please use a different email address.";
                    }
                    return true;
                  },
                })}
                required
                type="email"
              />
              <TextField
                autoComplete="tel"
                error={errors.phone?.message}
                label="Phone number"
                placeholder="07123 456789"
                registration={register("phone", {
                  validate: (value) =>
                    !value || isValidUkContactPhone(value)
                      ? true
                      : "Enter a valid UK mobile or landline number",
                })}
                type="tel"
              />
            </div>

            <div className="field-row">
              <SelectField
                error={errors.countryCode?.message}
                label="Country of residence"
                registration={register("countryCode", {
                  required: "Please enter your country of residence.",
                  validate: (v) => v === "GB" || "Must be a UK resident",
                })}
                required
                options={
                  countryCodes?.data?.map((code) => ({
                    value: code.countryCode,
                    label: code.name,
                  })) || []
                }
              />
              <TextField
                error={errors.dateOfBirth?.message}
                hint="You must be 18 or over to join."
                label="Date of birth"
                registration={register("dateOfBirth", {
                  required: "Please enter your date of birth.",
                  validate: (value) =>
                    isAdult(value) || "You must be 18 or over to sign up.",
                })}
                required
                type="date"
              />
            </div>

            {signupFieldsQuery.isLoading && (
              <SkeletonBlock count={4} height={42} />
            )}
            {signupFields && <DynamicSignupFields fieldsData={signupFields} />}

            <div className="section-lbl">Consent</div>
            <div
              className={`signup-consent-row ${errors.terms ? "has-error" : ""}`.trim()}
            >
              <input
                id="signup-terms"
                {...register("terms", {
                  validate: (value) =>
                    value ||
                    "You must accept the terms and privacy policy to continue.",
                })}
                type="checkbox"
              />
              <label htmlFor="signup-terms">
                I agree to the{" "}
                <button
                  className="policy-link"
                  onClick={(event) => {
                    event.preventDefault();
                    window.open(panelPath("terms"), "_blank");
                  }}
                  type="button"
                >
                  Terms & Conditions
                </button>{" "}
                and confirm I have read the{" "}
                <button
                  className="policy-link"
                  onClick={(event) => {
                    event.preventDefault();
                    window.open(panelPath("privacy"), "_blank");
                  }}
                  type="button"
                >
                  Privacy Policy
                </button>
                . I am 18 or over and a UK resident.
              </label>
            </div>
            {errors.terms?.message && (
              <div className="err-msg signup-consent-error">
                {errors.terms.message}
              </div>
            )}

            {turnstileSiteKey && (
              <>
                <div className="signup-turnstile" id={turnstileContainerId} />
                {turnstileError && (
                  <div className="err-msg signup-turnstile-error">
                    {turnstileError}
                  </div>
                )}
              </>
            )}

            <button
              className="btn btn-primary btn-full signup-submit"
              disabled={!canSubmit}
              type="submit"
            >
              {signUpPanelist.isPending ? "Submitting..." : "Complete sign up"}
            </button>
            <button
              className="btn btn-outline btn-full"
              type="button"
              onClick={onCancel}
            >
              Cancel
            </button>
          </form>
        </FormProvider>

        {signUpPanelist.error && (
          <ErrorState
            error={signUpPanelist.error}
            title="Could not sign you up"
          />
        )}
        {signupFieldsQuery.error && (
          <ErrorState
            error={signupFieldsQuery.error}
            title="Could not load signup fields"
          />
        )}
      </div>
    </AuthSignupShell>
  );
}

function AuthSignupShell({ children }: { children: ReactNode }) {
  return <main className="auth-shell signup-shell">{children}</main>;
}

function TextField({
  autoComplete,
  error,
  hint,
  label,
  placeholder,
  readOnly = false,
  registration,
  required = false,
  type = "text",
}: {
  autoComplete?: string;
  error?: string;
  hint?: string;
  label?: string;
  placeholder?: string;
  readOnly?: boolean;
  registration: UseFormRegisterReturn;
  required?: boolean;
  type?: string;
}) {
  const fieldId = `signup-${registration.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div className="field">
      {label && (
        <label htmlFor={fieldId}>
          {label} {required && <span className="req">*</span>}
        </label>
      )}
      <input
        autoComplete={autoComplete}
        className={error ? "has-error" : ""}
        id={fieldId}
        placeholder={placeholder}
        readOnly={readOnly}
        type={type}
        {...registration}
      />
      {hint ? <div className="field-hint">{hint}</div> : null}
      {error ? <div className="err-msg">{error}</div> : null}
    </div>
  );
}

function SelectField({
  error,
  hint,
  label,
  options,
  readOnly = false,
  registration,
  required = false,
  placeholder,
}: {
  error?: string;
  hint?: string;
  label?: string;
  placeholder?: string;
  readOnly?: boolean;
  registration: UseFormRegisterReturn;
  required?: boolean;
  options: Array<{
    value: string;
    label: string;
  }>;
}) {
  const fieldId = `signup-${registration.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div className="field">
      {label && (
        <label htmlFor={fieldId}>
          {label} {required && <span className="req">*</span>}
        </label>
      )}
      <select
        className={error ? "has-error" : ""}
        id={fieldId}
        disabled={readOnly}
        {...registration}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {hint ? <div className="field-hint">{hint}</div> : null}
      {error ? <div className="err-msg">{error}</div> : null}
    </div>
  );
}

function DynamicSignupFields({
  fieldsData,
}: {
  fieldsData: ProfileFieldsData;
}) {
  return (
    <>
      {fieldsData.sections.map((section) => (
        <div className="profile-field-section" key={section.title}>
          <div className="section-lbl">{section.title}</div>
          {buildProfileFieldRows(section.fields).map((row) => {
            const rowKey = row.map((field) => field.fieldKey).join(":");
            const isFullRow =
              row.length === 1 &&
              (row[0].width === "full" || isMultiValueDynamicField(row[0]));

            if (isFullRow) {
              return <SignupDynamicFieldControl field={row[0]} key={rowKey} />;
            }

            return (
              <div className="field-row" key={rowKey}>
                {row.map((field) => (
                  <SignupDynamicFieldControl
                    field={field}
                    key={field.fieldKey}
                  />
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
}

function SignupDynamicFieldControl({ field }: { field: ProfileField }) {
  return (
    <DynamicField<SignupFormValues>
      field={field}
      idPrefix="signup-pf"
      name={signupProfileFieldPath(field.fieldKey)}
      requiredIndicator
    />
  );
}

function readInitialSignupFieldValues(
  fieldsData: ProfileFieldsData,
): ProfileFieldValues {
  return fieldsData.sections
    .flatMap((section) => section.fields)
    .reduce<ProfileFieldValues>((values, field) => {
      values[field.fieldKey] = initialDynamicFieldValue(field);
      return values;
    }, {});
}

function signupProfileFieldPath(fieldKey: string): FieldPath<SignupFormValues> {
  return `profileFields.${fieldKey}` as FieldPath<SignupFormValues>;
}

function isAdult(value: string): boolean {
  const dob = new Date(`${value}T00:00:00`);
  if (Number.isNaN(dob.getTime())) return false;

  const today = new Date();
  const eighteenthBirthday = new Date(
    dob.getFullYear() + 18,
    dob.getMonth(),
    dob.getDate(),
  );
  return today >= eighteenthBirthday;
}
